import { API_CONFIG } from '../config/api';

class OrbitXService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.cache = new Map();
    this.cacheTimeout = 300000; // 5 minutes cache for exchange data
  }

  // Get available coins and pairs from OrbitX
  async getAvailableCoins() {
    try {
      const cacheKey = 'available_coins';
      const cached = this.getCachedData(cacheKey);
      if (cached) return cached;

      const response = await fetch(`${this.baseURL}${API_CONFIG.PUBLIC.COINS}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching available coins:', error);
      throw error;
    }
  }

  // Get available trading pairs
  async getAvailablePairs() {
    try {
      const data = await this.getAvailableCoins();
      return data.pairs_data || {};
    } catch (error) {
      console.error('Error fetching available pairs:', error);
      return {};
    }
  }

  // Get trading pairs specifically for charts (with better error handling)
  async getTradingPairs() {
    try {
      const cacheKey = 'trading_pairs';
      const cached = this.getCachedData(cacheKey);
      if (cached) return cached;

      const response = await fetch(`${this.baseURL}/v1/pairs`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching trading pairs:', error);
      // Fallback to the general method
      return await this.getAvailablePairs();
    }
  }

  // Get blockchain definitions
  async getBlockchainDefinitions() {
    try {
      const data = await this.getAvailableCoins();
      return data.commons?.blockchain_definitions || {};
    } catch (error) {
      console.error('Error fetching blockchain definitions:', error);
      return {};
    }
  }

  // Get token transaction explorers
  async getTokenTxExplorers() {
    try {
      const data = await this.getAvailableCoins();
      return data.commons?.token_tx_explorers || {};
    } catch (error) {
      console.error('Error fetching token explorers:', error);
      return {};
    }
  }

  // Check if a coin is available in the system
  async isCoinAvailable(coinSymbol) {
    try {
      const pairs = await this.getAvailablePairs();
      const upperSymbol = coinSymbol.toUpperCase();
      
      // Check if coin exists as base in any pair
      for (const pairKey in pairs) {
        const pair = pairs[pairKey];
        if (pair.pair_data?.base?.code === upperSymbol) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error checking coin availability:', error);
      return false;
    }
  }

  // Get available base coins
  async getAvailableBaseCoins() {
    try {
      const pairs = await this.getAvailablePairs();
      const baseCoins = new Set();
      
      for (const pairKey in pairs) {
        const pair = pairs[pairKey];
        if (pair.pair_data?.base?.code) {
          baseCoins.add(pair.pair_data.base.code);
        }
      }
      
      return Array.from(baseCoins);
    } catch (error) {
      console.error('Error fetching available base coins:', error);
      return [];
    }
  }

  // Get pair details for a specific coin
  async getPairDetails(coinSymbol) {
    try {
      const pairs = await this.getAvailablePairs();
      const upperSymbol = coinSymbol.toUpperCase();
      
      for (const pairKey in pairs) {
        const pair = pairs[pairKey];
        if (pair.pair_data?.base?.code === upperSymbol) {
          return pair;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching pair details:', error);
      return null;
    }
  }

  // Cache management
  getCachedData(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  setCachedData(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
  }

  // Get cache info
  getCacheInfo() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

export default new OrbitXService();
